import { useContext, useEffect, useState } from 'react';
import { Box, Link, Stack, Text } from '@ltvco/refresh-lib/theme';
import { commaFormat } from '@ltvco/refresh-lib/utils';
import { useApiDataPoints, useSession } from '@ltvco/refresh-lib/v1';
import { AppConstants } from '@ltvco/refresh-lib/ctx';
import { CreditCalculatorRow } from './CreditCalculatorRow/CreditCalculatorRow';
import {
  StyledCreditCalculatorCard,
  StyledRowHeader,
  StyledSuggestedPlanCard,
  StyledSuggestedPlanCardStack,
} from './components';
import { CostItem, CostTracker } from './interfaces';

/**
 * React component for the credit calculator.
 *
 * This component provides a calculator for estimating the number of credits
 * needed per month based on API usage. It includes a list of API endpoints
 * and input fields for users to enter their expected monthly calls.
 *
 * @returns {React.ReactElement} A React element representing the credit calculator.
 */
export const CreditCalculator = () => {
  const {
    links: { contactForm },
  } = useContext(AppConstants);
  const {
    session: { account: accountResponse },
  } = useSession();
  const { isLoading, data: apiDataPoints } = useApiDataPoints();
  const [costTracker, setCostTracker] = useState<CostTracker | null>(null);
  const [totalCost, setTotalCost] = useState(0);

  useEffect(() => {
    if (apiDataPoints) {
      const newCostTracker: Record<string, CostItem> = {};
      Object.entries(apiDataPoints).forEach(([key, value]) => {
        newCostTracker[key as string] = {
          title: key,
          input: 0,
          costValue: value as number,
          currentInputCost: 0,
        };
      });
      setCostTracker(newCostTracker);
    }
  }, [apiDataPoints]);

  /**
   * Updates the cost tracker for a given item.
   *
   * Modifies the cost tracker state to reflect changes in user input. Updates
   * the input value and calculates the current input cost for a specific API
   * request type.
   *
   * @param {string} title - The title of the item to update in the cost tracker.
   * @param {number} newInput - The new input value for the item.
   */
  const updateCostTracker = (title: string, newInput: number) => {
    // Check if costTracker exists and has the item (title) to update.
    if (!costTracker || !costTracker[title]) return;

    // Retrieve the current state of the item's cost tracker.
    const currentCostTracker = costTracker[title];

    // Update the item's input value.
    currentCostTracker.input = newInput;

    currentCostTracker.currentInputCost =
      newInput * currentCostTracker.costValue;

    const newCostTracker: CostTracker = {
      ...costTracker,
      [title]: currentCostTracker,
    };

    setCostTracker(newCostTracker);

    if (!newCostTracker) {
      setTotalCost(0);
    }

    // Calculate the total cost by summing up the currentInputCost of all items.
    const total: number = Object.values(newCostTracker).reduce((acc, curr) => {
      return acc + (curr?.currentInputCost || 0);
    }, 0);

    setTotalCost(total);
  };

  /**
   * Handles the contact us action by opening a new window with the contact form.
   * If the user is logged in, the email address is included in the URL.
   */
  const handleContactUs = () => {
    let url = contactForm;

    if (accountResponse?.account.user_info.email) {
      url += `?email_address=${encodeURIComponent(
        accountResponse.account.user_info.email
      )}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // JSX
  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <StyledCreditCalculatorCard variant="outlined">
        <Text variant="h2">Credit Calculator</Text>
        <Text
          sx={(theme) => ({ [theme.breakpoints.down('md')]: { mx: 2, my: 4 } })}
        >
          Use the calculator below to identify which raw data API plan is right
          for you. Enter the expected API calls per month to each endpoint and
          see a total of credits needed.
        </Text>
        <StyledRowHeader>
          <Text variant="body2">Request Type</Text>
          <Text variant="body2">Calls Monthly</Text>
          <Text variant="body2">Credits Monthly</Text>
        </StyledRowHeader>
        {apiDataPoints &&
          costTracker &&
          Object.entries(apiDataPoints).map(([key, value]) => {
            return (
              <CreditCalculatorRow
                key={`credit-calculator-row-${key}`}
                title={key}
                value={value as number}
                costTracker={costTracker}
                updateCostTracker={updateCostTracker}
              />
            );
          })}
      </StyledCreditCalculatorCard>
      <Stack
        sx={(theme) => ({
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row-reverse',
          },
        })}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ backgroundColor: 'background.default', padding: 2 }}
          width={{ xs: '100%', md: '20%' }}
        >
          <Text variant="h3">{commaFormat(totalCost)}</Text>
          <Link variant="body2" onClick={handleContactUs}>
            Contact Us
          </Link>
        </Stack>
        <StyledSuggestedPlanCard>
          <StyledSuggestedPlanCardStack>
            <Stack
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="center"
            >
              <Text variant="h4" mb={0}>
                Estimated Credits Needed Per Month
              </Text>
            </Stack>
          </StyledSuggestedPlanCardStack>
        </StyledSuggestedPlanCard>
      </Stack>
    </Box>
  );
};
